
import { PlatformInfo } from './platform-detector';

export interface PlatformStrategy {
  approachStrategy: string;
  escalationPath: string[];
  legalContext: string;
  timeframes: {
    initialResponse: string;
    escalation: string;
    finalAction: string;
  };
  specificTactics: string[];
}

export class PlatformStrategies {
  static getStrategy(platformInfo: PlatformInfo, amount: number, region?: string): PlatformStrategy {
    const baseStrategy = this.getBasePlatformStrategy(platformInfo.platform);
    const transactionStrategy = this.getTransactionTypeStrategy(platformInfo.transactionType);
    const amountStrategy = this.getAmountBasedStrategy(amount);
    
    return this.combineStrategies(baseStrategy, transactionStrategy, amountStrategy, platformInfo, region);
  }

  private static getBasePlatformStrategy(platform: PlatformInfo['platform']) {
    switch (platform) {
      case 'amazon':
        return {
          approachStrategy: 'Direct platform escalation with A-to-Z guarantee leverage',
          escalationPath: ['Seller contact', 'Amazon customer service', 'A-to-Z claim', 'Chargeback'],
          legalContext: 'Amazon marketplace buyer protection and A-to-Z guarantee',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '7 days',
            finalAction: '14 days'
          },
          specificTactics: [
            'Reference A-to-Z guarantee eligibility',
            'Mention Amazon marketplace standards',
            'Cite buyer protection policies'
          ]
        };

      case 'ebay':
        return {
          approachStrategy: 'eBay Money Back Guarantee and resolution center focus',
          escalationPath: ['Seller message', 'eBay resolution center', 'eBay customer service', 'Chargeback'],
          legalContext: 'eBay Money Back Guarantee and buyer protection',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '5 days',
            finalAction: '10 days'
          },
          specificTactics: [
            'Reference eBay Money Back Guarantee',
            'Mention item not as described protection',
            'Cite eBay resolution center options'
          ]
        };

      case 'etsy':
        return {
          approachStrategy: 'Etsy Purchase Protection with artisan consideration',
          escalationPath: ['Shop owner contact', 'Etsy customer service', 'Etsy case opening', 'Chargeback'],
          legalContext: 'Etsy Purchase Protection and marketplace policies',
          timeframes: {
            initialResponse: '72 hours',
            escalation: '7 days',
            finalAction: '14 days'
          },
          specificTactics: [
            'Reference Etsy Purchase Protection',
            'Mention support for small businesses while asserting rights',
            'Cite Etsy marketplace standards'
          ]
        };

      case 'shopify':
        return {
          approachStrategy: 'Direct merchant communication with payment processor backup',
          escalationPath: ['Store customer service', 'Shopify support notification', 'Payment processor dispute'],
          legalContext: 'Individual store return policy and payment processor rights',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '5 days',
            finalAction: '10 days'
          },
          specificTactics: [
            'Reference store return policy',
            'Mention payment processor protection',
            'Cite consumer protection laws'
          ]
        };

      case 'paypal':
        return {
          approachStrategy: 'PayPal Buyer Protection and dispute resolution',
          escalationPath: ['Seller contact', 'PayPal resolution center', 'PayPal dispute escalation', 'Claim'],
          legalContext: 'PayPal Buyer Protection and dispute resolution',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '20 days',
            finalAction: '180 days'
          },
          specificTactics: [
            'Reference PayPal Buyer Protection',
            'Mention eligible transaction protection',
            'Cite PayPal dispute resolution process'
          ]
        };

      // C2C Platforms
      case 'facebook_marketplace':
        return {
          approachStrategy: 'Direct seller communication with Facebook protection backup',
          escalationPath: ['Seller message', 'Facebook Commerce support', 'Payment dispute', 'Chargeback'],
          legalContext: 'Facebook Purchase Protection (if applicable) and payment processor rights',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '48 hours',
            finalAction: '7 days'
          },
          specificTactics: [
            'Reference Facebook Purchase Protection if applicable',
            'Mention peer-to-peer understanding',
            'Be respectful but firm about resolution'
          ]
        };

      case 'offerup':
        return {
          approachStrategy: 'Direct seller contact with TruYou verification consideration',
          escalationPath: ['Seller message', 'OfferUp support', 'Payment dispute'],
          legalContext: 'OfferUp TruYou protection and payment processor rights',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '48 hours',
            finalAction: '5 days'
          },
          specificTactics: [
            'Mention TruYou verification if applicable',
            'Reference OfferUp community standards',
            'Use understanding but assertive tone'
          ]
        };

      case 'craigslist':
        return {
          approachStrategy: 'Direct private seller communication with caution',
          escalationPath: ['Seller contact', 'Payment processor dispute', 'Local authorities if fraud'],
          legalContext: 'Limited platform protection - rely on payment processor and local laws',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '72 hours',
            finalAction: '7 days'
          },
          specificTactics: [
            'Be very respectful - no platform protection',
            'Focus on fairness and mutual resolution',
            'Document everything for potential fraud claims'
          ]
        };

      case 'mercari':
      case 'poshmark':
      case 'depop':
      case 'vinted':
        return {
          approachStrategy: 'Platform-mediated resolution with seller communication',
          escalationPath: ['Seller message', 'Platform support', 'Case opening', 'Payment dispute'],
          legalContext: 'Platform buyer protection and return policies',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '3 days',
            finalAction: '10 days'
          },
          specificTactics: [
            'Reference platform buyer protection',
            'Use friendly but clear communication',
            'Leverage platform dispute resolution'
          ]
        };

      case 'nextdoor':
      case 'varagesale':
      case 'fivemiles':
        return {
          approachStrategy: 'Neighborly communication with community standards',
          escalationPath: ['Direct seller contact', 'Community reporting', 'Payment dispute'],
          legalContext: 'Community standards and payment processor protection',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '48 hours',
            finalAction: '5 days'
          },
          specificTactics: [
            'Emphasize community trust and reputation',
            'Be extra respectful - neighbor relationships',
            'Focus on fair resolution'
          ]
        };

      case 'reverb':
        return {
          approachStrategy: 'Music community focused resolution',
          escalationPath: ['Seller message', 'Reverb Protection', 'Reverb support', 'Payment dispute'],
          legalContext: 'Reverb Protection and music community standards',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '7 days',
            finalAction: '14 days'
          },
          specificTactics: [
            'Reference Reverb Protection',
            'Appeal to music community values',
            'Use respectful musician-to-musician tone'
          ]
        };

      case 'kidizen':
      case 'tradesy':
      case 'chairish':
        return {
          approachStrategy: 'Specialized marketplace protection focus',
          escalationPath: ['Seller contact', 'Platform authentication team', 'Buyer protection claim'],
          legalContext: 'Platform authentication and buyer protection policies',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '4 days',
            finalAction: '10 days'
          },
          specificTactics: [
            'Reference platform authentication process',
            'Mention specialized marketplace standards',
            'Use category-appropriate tone'
          ]
        };

      case 'gunbroker':
        return {
          approachStrategy: 'Direct seller communication with industry considerations',
          escalationPath: ['Seller message', 'GunBroker dispute', 'FFL involvement if applicable'],
          legalContext: 'GunBroker policies and firearms transaction regulations',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '3 days',
            finalAction: '7 days'
          },
          specificTactics: [
            'Reference GunBroker community standards',
            'Mention firearms transaction requirements if applicable',
            'Use respectful collector-to-collector tone'
          ]
        };

      // Subscription Platform Strategies
      case 'apple_app_store':
        return {
          approachStrategy: 'Self-service portal first, then escalate to Apple Support',
          escalationPath: ['Report a Problem portal', 'Apple Support chat', 'Apple Support phone'],
          legalContext: 'App Store Terms and Conditions, consumer protection laws',
          timeframes: {
            initialResponse: 'Immediate (self-service)',
            escalation: '24 hours',
            finalAction: '7 days'
          },
          specificTactics: ['Use reportaproblem.apple.com first', 'Reference forgotten cancellation', 'Mention auto-renewal issues']
        };

      case 'google_play':
        return {
          approachStrategy: 'Google Play refund portal, then Google Support',
          escalationPath: ['Play Store account refund', 'Google Support'],
          legalContext: 'Google Play Terms of Service, consumer protection laws',
          timeframes: {
            initialResponse: 'Immediate (self-service)',
            escalation: '24 hours',
            finalAction: '7 days'
          },
          specificTactics: ['Use Play Store account page', 'Reference accidental subscription', 'Highlight app malfunction if applicable']
        };

      case 'netflix':
        return {
          approachStrategy: 'Direct contact with Netflix customer service',
          escalationPath: ['Netflix customer service', 'Account specialist', 'Chargeback'],
          legalContext: 'Netflix Terms of Service - limited refund policy',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '14 days'
          },
          specificTactics: ['Emphasize unauthorized charge', 'Reference forgotten cancellation', 'Be persistent but polite']
        };

      case 'spotify':
        return {
          approachStrategy: 'Spotify customer service direct contact',
          escalationPath: ['Spotify support', 'Account specialist', 'Chargeback'],
          legalContext: 'Spotify Terms of Service - limited refund policy',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '14 days'
          },
          specificTactics: ['Reference trial expiration without notice', 'Emphasize unexpected charge', 'Request goodwill refund']
        };

      case 'youtube_premium':
        return {
          approachStrategy: 'YouTube support through Google ecosystem',
          escalationPath: ['YouTube help center', 'Google Support'],
          legalContext: 'YouTube Premium Terms of Service, Google policies',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '7 days'
          },
          specificTactics: ['Reference billing error', 'Highlight service failure', 'Use Google support channels']
        };

      case 'adobe_creative_cloud':
        return {
          approachStrategy: 'Adobe customer service with 14-day policy leverage',
          escalationPath: ['Adobe customer service', 'Account specialist', 'Escalation team'],
          legalContext: 'Adobe Terms - 14-day refund policy for new subscriptions',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '14 days'
          },
          specificTactics: ['Reference 14-day policy', 'Emphasize mistaken subscription', 'Request early termination fee waiver']
        };

      case 'canva_pro':
        return {
          approachStrategy: 'Canva support with first-time upgrade policy',
          escalationPath: ['Canva customer service', 'Account specialist'],
          legalContext: 'Canva Terms - 14-day refund for first-time upgrades',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '14 days'
          },
          specificTactics: ['Reference first-time upgrade policy', 'Mention forgotten trial cancellation', 'Request goodwill refund']
        };

      case 'notion':
        return {
          approachStrategy: 'Notion support with 72-hour annual plan policy',
          escalationPath: ['Notion customer service', 'Account team'],
          legalContext: 'Notion Terms - 72-hour refund window for annual plans',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '7 days'
          },
          specificTactics: ['Reference 72-hour policy for annual plans', 'Emphasize recent charge', 'Request policy exception if needed']
        };

      case 'grammarly':
        return {
          approachStrategy: 'Grammarly support with 10-day annual policy',
          escalationPath: ['Grammarly customer service', 'Account specialist'],
          legalContext: 'Grammarly Terms - 10-day refund for annual plans',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '10 days'
          },
          specificTactics: ['Reference 10-day policy', 'Mention mistaken upgrade', 'Request prorated refund if applicable']
        };

      case 'squarespace':
        return {
          approachStrategy: 'Squarespace support with 14-day annual policy',
          escalationPath: ['Squarespace customer service', 'Billing specialist'],
          legalContext: 'Squarespace Terms - 14-day refund for annual plans only',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '14 days'
          },
          specificTactics: ['Reference 14-day annual plan policy', 'Note domain/monthly fees are non-refundable', 'Focus on annual plan portion']
        };

      case 'nordvpn':
        return {
          approachStrategy: 'NordVPN support with 30-day money-back guarantee',
          escalationPath: ['NordVPN customer service', 'Billing team'],
          legalContext: 'NordVPN Terms - 30-day money-back guarantee',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '30 days'
          },
          specificTactics: ['Reference 30-day guarantee', 'Ensure no usage abuse cited', 'Request full refund under guarantee']
        };

      case 'expressvpn':
        return {
          approachStrategy: 'ExpressVPN support with 30-day money-back guarantee',
          escalationPath: ['ExpressVPN customer service', 'Account team'],
          legalContext: 'ExpressVPN Terms - 30-day money-back guarantee',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3 days',
            finalAction: '30 days'
          },
          specificTactics: ['Reference 30-day guarantee', 'Emphasize manual cancellation requirement', 'Request immediate processing']
        };

      case 'upwork':
        return {
          approachStrategy: 'Platform dispute system with milestone protection',
          escalationPath: ['Dispute Resolution', 'Customer Support', 'Arbitration'],
          legalContext: 'Upwork Terms of Service and milestone protection',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3-5 days',
            finalAction: '2 weeks'
          },
          specificTactics: ['File dispute through platform', 'Document work quality issues', 'Reference milestone agreements']
        };

      case 'fiverr':
        return {
          approachStrategy: 'Resolution Center with buyer protection focus',
          escalationPath: ['Resolution Center', 'Customer Support', 'Mediation'],
          legalContext: 'Fiverr Terms of Service and buyer protection',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3-5 days',
            finalAction: '13 days'
          },
          specificTactics: ['Use Resolution Center', 'Document delivery issues', 'Reference gig requirements']
        };

      case 'freelancer':
        return {
          approachStrategy: 'Platform dispute system with arbitration option',
          escalationPath: ['Dispute System', 'Customer Support', 'Arbitration'],
          legalContext: 'Freelancer.com dispute resolution policy',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '5-7 days',
            finalAction: '2 weeks'
          },
          specificTactics: ['Use platform dispute system', 'Document project requirements', 'Request arbitration if needed']
        };

      case 'peopleperhour':
        return {
          approachStrategy: 'Dispute resolution with moderation team support',
          escalationPath: ['Dispute Resolution', 'Moderation Team', 'Customer Support'],
          legalContext: 'PeoplePerHour Terms and buyer protection',
          timeframes: {
            initialResponse: '24 hours',
            escalation: '3-5 days',
            finalAction: '7 days'
          },
          specificTactics: ['Raise dispute within 7 days', 'Document delivery issues', 'Work with moderation team']
        };

      case 'taskrabbit':
        return {
          approachStrategy: 'TaskRabbit Happiness Pledge with quick resolution',
          escalationPath: ['Customer Support', 'Task Dispute Team', 'Resolution Specialist'],
          legalContext: 'TaskRabbit Happiness Pledge and task protection',
          timeframes: {
            initialResponse: '2-4 hours',
            escalation: '24 hours',
            finalAction: '48 hours'
          },
          specificTactics: ['Contact within 24 hours', 'Document task completion issues', 'Reference TaskRabbit guarantees']
        };

      default:
        return {
          approachStrategy: 'Standard consumer protection approach',
          escalationPath: ['Direct contact', 'Consumer protection agency', 'Chargeback/legal action'],
          legalContext: 'General consumer protection laws',
          timeframes: {
            initialResponse: '48 hours',
            escalation: '7 days',
            finalAction: '14 days'
          },
          specificTactics: [
            'Reference consumer protection laws',
            'Mention chargeback rights',
            'Cite return policy obligations'
          ]
        };
    }
  }

  private static getTransactionTypeStrategy(transactionType: 'B2C' | 'C2C' | 'subscription' | 'gig_platform' | 'event_ticketing' | 'accommodation') {
    if (transactionType === 'B2C') {
      return {
        tone: 'Professional and firm with business expectations',
        legalWeight: 'Strong consumer protection laws',
        expectations: 'Professional customer service standards'
      };
    } else if (transactionType === 'subscription') {
      return {
        tone: 'Professional and policy-focused with subscription terms',
        legalWeight: 'Subscription terms, auto-renewal laws, and consumer protection',
        expectations: 'Adherence to subscription policies and refund windows'
      };
    } else if (transactionType === 'gig_platform') {
      return {
        tone: 'Professional and dispute-focused with platform mediation',
        legalWeight: 'Platform terms of service and dispute resolution policies',
        expectations: 'Fair resolution through platform dispute mechanisms'
      };
    } else if (transactionType === 'event_ticketing') {
      return {
        tone: 'Direct and policy-focused with event cancellation emphasis',
        legalWeight: 'Ticket terms of sale and consumer protection for canceled events',
        expectations: 'Refunds only for canceled events or ticket invalidity'
      };
    } else if (transactionType === 'accommodation') {
      return {
        tone: 'Professional with cancellation policy reference',
        legalWeight: 'Booking terms, cancellation policies, and consumer protection',
        expectations: 'Resolution based on specific cancellation policy terms'
      };
    } else {
      return {
        tone: 'Respectful but assertive with individual consideration',
        legalWeight: 'Platform protection and payment processor rights',
        expectations: 'Fair resolution with understanding of individual seller constraints'
      };
    }
  }

  private static getAmountBasedStrategy(amount: number) {
    if (amount > 500) {
      return {
        urgency: 'high',
        legalThreat: 'strong',
        escalationSpeed: 'fast'
      };
    } else if (amount > 100) {
      return {
        urgency: 'medium',
        legalThreat: 'moderate',
        escalationSpeed: 'normal'
      };
    } else {
      return {
        urgency: 'low',
        legalThreat: 'light',
        escalationSpeed: 'patient'
      };
    }
  }

  private static combineStrategies(
    baseStrategy: any,
    transactionStrategy: any,
    amountStrategy: any,
    platformInfo: PlatformInfo,
    region?: string
  ): PlatformStrategy {
    const combinedTactics = [
      ...baseStrategy.specificTactics,
      `Use ${transactionStrategy.tone.toLowerCase()}`,
      `Apply ${amountStrategy.urgency} urgency approach`
    ];

    if (platformInfo.isPrivateSeller) {
      combinedTactics.push('Acknowledge individual seller while maintaining firm position');
    }

    if (platformInfo.platformSpecificData.hasMarketplaceProtection) {
      combinedTactics.push('Leverage marketplace buyer protection programs');
    }

    return {
      approachStrategy: baseStrategy.approachStrategy,
      escalationPath: baseStrategy.escalationPath,
      legalContext: `${baseStrategy.legalContext}. ${transactionStrategy.legalWeight}.`,
      timeframes: baseStrategy.timeframes,
      specificTactics: combinedTactics
    };
  }
}
